import React, { useState, useEffect, useMemo } from 'react';
import {
  PlusCircle, RefreshCw, Truck, Clock, CheckCircle2, AlertTriangle,
  Calendar, ArrowRight, Sparkles, ListChecks, DollarSign, BarChart3
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useClientPermissions } from '../hooks/useClientPermissions';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import KpiCard from '../components/ui-kit/KpiCard';
import EmptyState from '../components/ui-kit/EmptyState';
import { StatusBadge, StatusLegendDrawer } from '../components/ui-kit/StatusBadge';
import { KpiCustomizerDrawer, useKpiVisibility } from '../components/dashboard/KpiCustomizerDrawer';
import { OperationDetailPanel } from '../components/dashboard/OperationDetailPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtNumber = (n) => {
  if (n === null || n === undefined) return '0';
  return new Intl.NumberFormat('fr-FR').format(n);
};
const fmtCost = (n) => {
  if (!n) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + ' M';
  if (n >= 1000) return (n / 1000).toFixed(0) + ' k';
  return new Intl.NumberFormat('fr-FR').format(n);
};

const STATUS_PIE_COLORS = {
  livrees: '#10b981',
  en_cours: '#f59e0b',
  en_retard: '#ef4444',
  autres: '#94a3b8',
};
const STATUS_PIE_LABELS = {
  livrees: 'Livrées',
  en_cours: 'En cours',
  en_retard: 'En retard',
  autres: 'Autres',
};

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = useClientPermissions();

  const [data, setData] = useState(null);
  const [recentOps, setRecentOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOpId, setSelectedOpId] = useState(null);

  // KPI definitions for personnalisation
  const KPI_DEFS = useMemo(() => [
    { key: 'total', label: 'Opérations totales', description: 'Nombre total d\'opérations sur la période', icon: Truck, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', defaultVisible: true },
    { key: 'inProgress', label: 'En cours', description: 'Opérations actuellement en cours', icon: Clock, iconBg: 'bg-amber-100', iconColor: 'text-amber-600', defaultVisible: true },
    { key: 'completed', label: 'Livrées', description: 'Opérations terminées avec succès', icon: CheckCircle2, iconBg: 'bg-blue-100', iconColor: 'text-blue-600', defaultVisible: true },
    { key: 'late', label: 'En retard', description: 'Opérations avec dépassement de délai', icon: AlertTriangle, iconBg: 'bg-red-100', iconColor: 'text-red-600', defaultVisible: true },
    { key: 'cost', label: 'Coût total', description: 'Montant total facturé sur la période', icon: DollarSign, iconBg: 'bg-green-100', iconColor: 'text-green-600', defaultVisible: true },
  ], []);

  const [kpiVisibility, toggleKpi, resetKpi] = useKpiVisibility(KPI_DEFS);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashRes, opsRes] = await Promise.all([
        axios.get(`${API}/client/dashboard-v2`),
        axios.get(`${API}/transport-requests?limit=5`).catch(() => ({ data: [] })),
      ]);
      setData(dashRes.data);
      const ops = Array.isArray(opsRes.data) ? opsRes.data : (opsRes.data?.items || []);
      setRecentOps(ops.slice(0, 4));
    } catch (e) {
      console.error('Dashboard error:', e);
      setError('Impossible de charger le tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const stats = useMemo(() => {
    const ov = data?.overview || {};
    return {
      total: ov.totalOperations || 0,
      inProgress: ov.activeOperations || 0,
      completed: ov.totalCompleted || 0,
      late: ov.lateOperations || 0,
      cost: ov.totalCost || 0,
      completionRate: ov.completionRate || 0,
      breakdown: ov.statusBreakdownGlobal || {},
    };
  }, [data]);

  // KPIs séparés : spot vs contrat (sources distinctes côté backend)
  const spotStats = useMemo(() => {
    const s = data?.spot || {};
    const bd = s.statusBreakdown || {};
    return {
      total: s.totalOps || 0,
      inProgress: (bd.enCours || 0) + (bd.preparation || 0),
      completed: bd.terminees || 0,
    };
  }, [data]);

  const contractStats = useMemo(() => {
    const contracts = data?.contracts || [];
    let totalRuns = 0;
    let completedRuns = 0;
    let activeRuns = 0;
    contracts.forEach((c) => {
      const k = c?.kpis || {};
      totalRuns += k.totalRuns || 0;
      completedRuns += k.completedRuns || 0;
      // active = totalRuns - completed - cancelled (approx)
      const cancelled = k.cancelledRuns || 0;
      activeRuns += Math.max(0, (k.totalRuns || 0) - (k.completedRuns || 0) - cancelled);
    });
    return {
      hasContracts: contracts.length > 0,
      total: totalRuns,
      inProgress: activeRuns,
      completed: completedRuns,
      contractsCount: contracts.length,
    };
  }, [data]);

  // Données pour le donut chart
  const pieData = useMemo(() => {
    return Object.entries(stats.breakdown)
      .filter(([_, v]) => v > 0)
      .map(([key, value]) => ({
        name: STATUS_PIE_LABELS[key] || key,
        value,
        color: STATUS_PIE_COLORS[key] || '#94a3b8',
      }));
  }, [stats.breakdown]);

  // Évolution réelle depuis le backend (weeklyTimeline) — fallback sur estimation si vide
  const lineData = useMemo(() => {
    const weekly = data?.weeklyTimeline;
    if (Array.isArray(weekly) && weekly.length > 0) {
      return weekly.map((w) => ({
        week: w.week,
        'Livrées': w.Livrees || 0,
        'En cours': w['En cours'] || 0,
        'En retard': w['En retard'] || 0,
      }));
    }
    return [];
  }, [data]);

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="dashboard-error">
        <p className="text-slate-500 mb-4">{error}</p>
        <Button onClick={fetchDashboard} variant="outline" className="gap-2" data-testid="retry-dashboard-btn">
          <RefreshCw className="w-4 h-4" /> Réessayer
        </Button>
      </div>
    );
  }

  const { overview } = data || {};
  const hasData = overview?.hasContracts || overview?.hasSpotOps;

  return (
    <div className="space-y-6" data-testid="client-dashboard-v3">
      {/* Hero conversion — dégradé vert Baobab premium */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0A3B2B] via-[#11825e] to-[#1db584] p-6 md:p-8 text-white shadow-[0_10px_40px_-10px_rgba(29,181,132,0.45)]" data-testid="dashboard-hero">
        <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full bg-white/10 blur-2xl pointer-events-none" />
        <div className="absolute right-24 -bottom-10 w-40 h-40 rounded-full bg-white/5 blur-xl pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-white/70">Espace expéditeur</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">
              Bonjour, {user?.firstName || 'cher client'}
            </h1>
            <p className="text-sm text-white/80 mt-1">
              Voici votre centre de pilotage logistique
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <KpiCustomizerDrawer
              definitions={KPI_DEFS}
              visibility={kpiVisibility}
              onToggle={toggleKpi}
              onReset={resetKpi}
            />
            <StatusLegendDrawer />
            <Button variant="outline" size="sm" onClick={fetchDashboard} className="gap-2 bg-white/10 border-white/25 text-white hover:bg-white/20 hover:text-white" data-testid="refresh-dashboard">
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Actualiser</span>
            </Button>
            {permissions.canCreateOperations && (
              <Button
                onClick={() => navigate('/nouvelle-demande')}
                className="bg-white text-[#11825e] hover:bg-white/90 font-semibold shadow-lg hover:-translate-y-0.5 transition-all"
                data-testid="new-request-btn"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Nouvelle demande
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* KPI Grid — DEMANDES SPOT (transport_requests) */}
      {contractStats.hasContracts && (
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-2" data-testid="kpi-section-spot-title">
          Demandes spot
        </h2>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4" data-testid="kpi-grid">
        {kpiVisibility.total && (
          <KpiCard
            label={contractStats.hasContracts ? 'Demandes spot' : 'Opérations totales'}
            value={fmtNumber(spotStats.total)}
            icon={Truck}
            variant="emerald"
            onClick={() => navigate('/operations')}
            testId="kpi-total-ops"
          />
        )}
        {kpiVisibility.inProgress && (
          <KpiCard
            label="En cours"
            value={fmtNumber(spotStats.inProgress)}
            icon={Clock}
            variant="amber"
            onClick={() => navigate('/operations?status=en_cours')}
            testId="kpi-in-progress"
          />
        )}
        {kpiVisibility.completed && (
          <KpiCard
            label="Livrées"
            value={fmtNumber(spotStats.completed)}
            icon={CheckCircle2}
            variant="blue"
            onClick={() => navigate('/operations?status=livree')}
            testId="kpi-delivered"
          />
        )}
        {kpiVisibility.late && (
          <KpiCard
            label="En retard"
            value={fmtNumber(stats.late)}
            icon={AlertTriangle}
            variant="red"
            onClick={() => navigate('/operations?status=en_retard')}
            testId="kpi-late"
          />
        )}
        {kpiVisibility.cost && (
          <KpiCard
            label="Coût total"
            value={fmtCost(stats.cost)}
            unit="FCFA"
            icon={DollarSign}
            variant="green"
            onClick={() => navigate('/finances')}
            testId="kpi-cost"
          />
        )}
      </div>

      {/* KPI Grid — OPÉRATIONS SOUS CONTRAT (contract_runs) — visible uniquement si le client a des contrats */}
      {contractStats.hasContracts && (
        <>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-2 flex items-center gap-2" data-testid="kpi-section-contracts-title">
            <ListChecks className="w-3.5 h-3.5" />
            Opérations sous contrat
            <span className="font-normal text-slate-400 normal-case tracking-normal">
              ({contractStats.contractsCount} contrat{contractStats.contractsCount > 1 ? 's' : ''} actif{contractStats.contractsCount > 1 ? 's' : ''})
            </span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4" data-testid="kpi-grid-contracts">
            <KpiCard
              label="Opérations contrat"
              value={fmtNumber(contractStats.total)}
              icon={Truck}
              variant="emerald"
              onClick={() => navigate('/mes-contrats')}
              testId="kpi-contract-total"
            />
            <KpiCard
              label="En cours"
              value={fmtNumber(contractStats.inProgress)}
              icon={Clock}
              variant="amber"
              onClick={() => navigate('/mes-contrats')}
              testId="kpi-contract-in-progress"
            />
            <KpiCard
              label="Terminées"
              value={fmtNumber(contractStats.completed)}
              icon={CheckCircle2}
              variant="blue"
              onClick={() => navigate('/mes-contrats')}
              testId="kpi-contract-completed"
            />
            <KpiCard
              label="Total combiné"
              value={fmtNumber(spotStats.total + contractStats.total)}
              icon={BarChart3}
              variant="slate"
              onClick={() => navigate('/analytics')}
              testId="kpi-combined-total"
            />
          </div>
        </>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Évolution opérations */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E2EBE7] p-6 bb-card-shadow bb-card-shadow-hover transition-all duration-300">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-emerald-600" />
                Évolution des opérations
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Tendance sur 6 semaines</p>
            </div>
          </div>
          <div className="h-[260px]">
            {lineData.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <EmptyState icon={BarChart3} title="Pas encore de données" description="Les tendances apparaîtront dès vos premières opérations." variant="compact" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="Livrées" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="En cours" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="En retard" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Donut répartition */}
        <div className="bg-white rounded-2xl border border-[#E2EBE7] p-6 bb-card-shadow bb-card-shadow-hover transition-all duration-300">
          <h2 className="text-base font-semibold text-slate-900 mb-3">Répartition par statut</h2>
          {pieData.length === 0 ? (
            <EmptyState icon={Sparkles} title="Pas encore de données" variant="compact" />
          ) : (
            <div className="h-[260px] relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={85}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              {/* Total au centre */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-slate-900">{stats.total}</span>
                <span className="text-[10px] text-slate-500 uppercase tracking-wide">Total</span>
              </div>
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-2">
            {pieData.map((p) => (
              <div key={p.name} className="flex items-center gap-1.5 text-xs">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
                <span className="text-slate-600">{p.name}</span>
                <span className="text-slate-400 ml-auto">{p.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance + Operations récentes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E2EBE7] p-6 bb-card-shadow bb-card-shadow-hover transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                Performance globale
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Sur l'ensemble de vos opérations</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/analytics')} className="text-emerald-600 hover:text-emerald-700">
              Voir détails <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-700 font-medium">Taux de complétion</span>
                <span className="text-2xl font-bold text-slate-900">{stats.completionRate}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all"
                  style={{ width: `${Math.min(100, stats.completionRate)}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                {stats.completed} opération{stats.completed > 1 ? 's' : ''} terminée{stats.completed > 1 ? 's' : ''} sur {stats.total} au total.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
              <button
                onClick={() => navigate('/mes-contrats')}
                className="text-left p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition"
                data-testid="dashboard-contracts-tile"
              >
                <ListChecks className="w-4 h-4 text-emerald-600 mb-1.5" />
                <p className="text-xs text-slate-500">Contrats actifs</p>
                <p className="text-lg font-semibold text-slate-900">{(data?.contracts || []).length}</p>
              </button>
              <button
                onClick={() => navigate('/finances')}
                className="text-left p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition"
                data-testid="dashboard-finances-tile"
              >
                <DollarSign className="w-4 h-4 text-blue-600 mb-1.5" />
                <p className="text-xs text-slate-500">Suivi financier</p>
                <p className="text-sm font-medium text-slate-700">Voir factures →</p>
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E2EBE7] p-6 bb-card-shadow bb-card-shadow-hover transition-all duration-300">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-slate-900">Opérations récentes</h2>
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate('/operations')}
              className="text-emerald-600 hover:text-emerald-700 text-xs h-auto py-1"
              data-testid="dashboard-see-all-ops"
            >
              Voir toutes
            </Button>
          </div>

          {recentOps.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title="Aucune opération"
              description="Créez votre première demande de transport pour commencer."
              actionLabel={permissions.canCreateOperations ? 'Nouvelle demande' : undefined}
              onAction={permissions.canCreateOperations ? () => navigate('/nouvelle-demande') : undefined}
              variant="compact"
            />
          ) : (
            <ul className="space-y-2">
              {recentOps.map((op) => (
                <li key={op.id}>
                  <button
                    onClick={() => setSelectedOpId(op.id)}
                    className="w-full text-left flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition group"
                    data-testid={`recent-op-${op.id}`}
                  >
                    <div className="w-8 h-8 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                      <Truck className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {op.operationNumber || `Op #${op.id?.slice(0, 8)}`}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {(op.departure || '—').split(',')[0]} → {(op.arrival || '—').split(',')[0]}
                      </p>
                    </div>
                    <StatusBadge status={op.status} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {!hasData && (
        <EmptyState
          icon={Truck}
          title="Aucune opération trouvée"
          description="Créez votre première demande de transport pour démarrer le pilotage de votre logistique."
          actionLabel={permissions.canCreateOperations ? 'Nouvelle demande' : undefined}
          onAction={permissions.canCreateOperations ? () => navigate('/nouvelle-demande') : undefined}
          testId="dashboard-empty"
        />
      )}

      {/* Slide-over Détail opération */}
      <OperationDetailPanel
        operationId={selectedOpId}
        open={!!selectedOpId}
        onClose={() => setSelectedOpId(null)}
      />
    </div>
  );
};

export default ClientDashboard;
